/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import type { SpeedTestResult } from "@/types/types";

export interface ServerStats {
  name: string;
  /** oldest -> newest */
  rows: SpeedTestResult[];
  latest: SpeedTestResult;
  avgDown: number;
  bestDown: number;
  avgUp: number;
  avgPing: number;
}

export const pingOf = (r: SpeedTestResult): number => parseFloat(r.latency) || 0;

export const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);

/** Groups test results (newest first, as the API returns them) by server, best average first. */
export function groupByServer(rowsNewestFirst: SpeedTestResult[]): ServerStats[] {
  const map = new Map<string, SpeedTestResult[]>();
  for (const r of rowsNewestFirst) {
    if (!(r.downloadSpeed >= 1)) continue; // a failed leg is not a measurement
    const list = map.get(r.serverName) ?? [];
    list.push(r);
    map.set(r.serverName, list);
  }
  return [...map.entries()]
    .map(([name, newestFirst]) => {
      const rows = [...newestFirst].reverse();
      return {
        name,
        rows,
        latest: newestFirst[0],
        avgDown: mean(rows.map((r) => r.downloadSpeed)),
        bestDown: Math.max(...rows.map((r) => r.downloadSpeed)),
        avgUp: mean(rows.map((r) => r.uploadSpeed)),
        avgPing: mean(rows.map(pingOf)),
      };
    })
    .sort((a, b) => b.avgDown - a.avgDown);
}

